import { AutocompleteField } from "components/AutocompleteField";
import { MapAddressSuggestion } from "features/dt-basic-setup/single-building/model/types";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import type { AddressSearchAutocompleteProps } from "./types";

import { MIN_QUERY_LENGTH } from "./constants";
import { useSetOptionsOnInputChangeEffect } from "./hooks/useSetOptionsOnInputChangeEffect";

const optionLabel = (o: MapAddressSuggestion) => (o.secondary ? `${o.primary}, ${o.secondary}` : o.primary);

export const AddressSearchAutocomplete = ({
  className,
  disabled = false,
  onSelect,
  popperDisablePortal,
}: AddressSearchAutocompleteProps) => {
  const { t } = useTranslation();
  const noOptionsText = t("AddBuilding.addressStep.search.noResults");
  const placeholder = t("AddBuilding.addressStep.search.placeholder");
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<MapAddressSuggestion[]>([]);

  const { loading } = useSetOptionsOnInputChangeEffect(setOptions, inputValue, MIN_QUERY_LENGTH);

  const handleChange = useCallback(
    (_: unknown, value: MapAddressSuggestion | null) => {
      if (!value) return;
      onSelect(value);
      setInputValue("");
      setOptions([]);
    },
    [onSelect]
  );

  return (
    <div className={className}>
      <AutocompleteField<MapAddressSuggestion>
        disabled={disabled}
        getOptionLabel={(o) => (typeof o === "string" ? o : optionLabel(o))}
        inputValue={inputValue}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        loading={loading}
        noOptionsText={inputValue.trim().length < MIN_QUERY_LENGTH ? "" : noOptionsText}
        onChange={(_, v) => handleChange(_, v as MapAddressSuggestion | null)}
        onInputChange={(_, v) => setInputValue(v ?? "")}
        options={options}
        placeholder={placeholder}
        popperDisablePortal={popperDisablePortal}
        value={null}
      />
    </div>
  );
};
